/**
 * The Sellwild Deal Score callout: what kind of deal the buyer is looking at,
 * as a badge or as a gauge. Presentational; useDealCallout decides the arm.
 *
 * Every number on it comes from the listing's own price analysis against its
 * current price (pages/api/deal-callout.ts). The words say "our estimate",
 * because that is what the reference is: most are a model's market estimate,
 * not a sold comp.
 */
import { Tag } from 'lucide-react';
import { dealLabel, formatDealDollars } from '../../utils/deal-callout';
import type { DealCallout as DealCalloutData, DealCalloutArm } from '../../schemas/api/deal-callout';

/** The buyer guide on what the score means (guides/buyer/deal-score.mdx). Opens in a new tab. */
export const DEAL_GUIDE_PATH = '/guides/buyer/deal-score';

/** The score bands the gauge is drawn in, matching dealLabel. */
export const GAUGE_BANDS = [
  { from: 0, to: 25, className: 'band-good' },
  { from: 25, to: 60, className: 'band-great' },
  { from: 60, to: 100, className: 'band-exceptional' },
] as const;

const CX = 100;
const CY = 100;
const R = 80;

/** A point on the dial for a score: 0 is the left end, 100 the right. */
export function gaugePoint(score: number, radius = R): { x: number; y: number } {
  const theta = Math.PI * (1 - Math.max(0, Math.min(100, score)) / 100);
  return { x: CX + radius * Math.cos(theta), y: CY - radius * Math.sin(theta) };
}

/** The arc for one band, with a sliver of gap either side. */
export function bandPath(from: number, to: number): string {
  const a = gaugePoint(from + (from === 0 ? 0 : 0.8));
  const b = gaugePoint(to - (to === 100 ? 0 : 0.8));
  const f = (n: number) => n.toFixed(2);
  return `M ${f(a.x)} ${f(a.y)} A ${R} ${R} 0 0 1 ${f(b.x)} ${f(b.y)}`;
}

function Gauge({ score }: { score: number }) {
  const tip = gaugePoint(score, R - 18);
  return (
    <svg className="deal-gauge" viewBox="0 0 200 112" role="img" aria-label={`Deal score ${score} out of 100`} data-testid="deal-gauge">
      {GAUGE_BANDS.map(band => (
        <path
          key={band.className}
          d={bandPath(band.from, band.to)}
          className={`deal-gauge-band ${band.className}${score >= band.from && (score < band.to || band.to === 100) ? ' is-current' : ''}`}
        />
      ))}
      <line x1={CX} y1={CY} x2={tip.x.toFixed(2)} y2={tip.y.toFixed(2)} className="deal-gauge-needle" data-testid="deal-gauge-needle" />
      <circle cx={CX} cy={CY} r={7} className="deal-gauge-hub" />
    </svg>
  );
}

export interface DealCalloutProps {
  arm: DealCalloutArm;
  callout: DealCalloutData | null;
  /** `slot` sits in the grid beside the ad; `inline` rides in the description on narrow screens. */
  placement: 'slot' | 'inline';
}

export default function DealCallout({ arm, callout, placement }: DealCalloutProps) {
  if (arm === 'none' || !callout) return null;

  const percent = `${callout.percentBelow}% below market`;
  const save = `Save about ${formatDealDollars(callout.savings)}`;
  const basis = `Against our ${formatDealDollars(callout.reference)} market estimate`;

  return (
    <aside
      className={`deal-callout deal-callout--${arm} deal-callout--${placement} same-color-2 same-color-border`}
      aria-label="Sellwild Deal Score"
      data-testid={`deal-callout-${arm}-${placement}`}
    >
      <h3 className="deal-callout-title">Sellwild Deal Score</h3>
      {arm === 'badge' ? (
        <div className="deal-badge">
          <span className="deal-badge-icon" aria-hidden="true">
            <Tag />
          </span>
          <div className="deal-badge-text">
            <p className="deal-callout-headline">{percent}</p>
            <p className="deal-callout-save">{save}</p>
          </div>
        </div>
      ) : (
        <div className="deal-gauge-wrap">
          <Gauge score={callout.score} />
          <p className="deal-callout-headline">{dealLabel(callout.score)}</p>
          <p className="deal-callout-save">
            {percent}, {save.toLowerCase()}
          </p>
        </div>
      )}
      <p className="deal-callout-basis">{basis}</p>
      <a className="deal-callout-guide" href={DEAL_GUIDE_PATH} target="_blank" rel="noopener noreferrer">
        How we find deals
      </a>
    </aside>
  );
}
